import { from, forkJoin } from 'rxjs';
import { mergeMap, mergeAll, toArray, filter } from 'rxjs/operators';

var MessagingPlatformService = /** @class */ (function () {
    function MessagingPlatformService() {
        this.activeProviderAdapters = new Map();
    }
    /**
     * Enables a messaging provider
     * After enabling a new provider, a call to getConversations will be required to update the conversations list
     * @param messageType the messaging type to enable
     * @param adapter the properly configured adapter the provider will use
     * @returns messaging platform service object
     */
    MessagingPlatformService.prototype.addProvider = function (messageType, adapter) {
        if (this.activeProviderAdapters.has(messageType)) {
            throw new Error("Error in messaging platform service: Provider for message type " + messageType + " already configured");
        }
        this.activeProviderAdapters.set(messageType, adapter);
        return this;
    };
    /**
     * @returns an array of all enabled messaging providers
     */
    MessagingPlatformService.prototype.getActiveProviders = function () {
        return Array.from(this.activeProviderAdapters.keys());
    };
    /**
     * @param count number of conversations to be returned
     * @param pageIdentifiers paginated identifier
     * @param folder represents a folder where the conversation will be allocated
     * @returns all the conversations in which the recipient is involved in active providers
     */
    MessagingPlatformService.prototype.getConversations = function (count, pageIdentifiers, folder) {
        return from(this.activeProviderAdapters.values()).pipe(mergeMap(function (adapter) { return adapter.getConversations(count, pageIdentifiers, folder); }));
    };
    /**
     * @param conversationId id of the conversation
     * @returns the conversation for a conversation id
     */
    MessagingPlatformService.prototype.getConversation = function (conversationId, messageType) {
        return this.getAdapter(messageType).getConversation(conversationId);
    };
    /**
     * SortBy.REVERSE + range = [0, 49] means the last 50 messages
     * @param conversationId unique identifier to represent a conversation
     * @param messageType the messaging type to enable
     * @param sortBy OLDEST_FIRST or NEWEST_FIRST (by default)
     * @param count number of conversations to be returned
     * @param pageIdentifier paginated identifier
     * @returns the messages of a specific conversation
     */
    MessagingPlatformService.prototype.getConversationMessages = function (conversationId, messageType, count, pageIdentifier) {
        return this.getAdapter(messageType).getConversationMessages(conversationId, count, pageIdentifier);
    };
    /**
     * Sends a message to an array of recipients
     * @param recipients the recipients identifying the conversation
     * @param body the content of the message
     * @returns sent message
     */
    MessagingPlatformService.prototype.send = function (recipients, messageType, body, correlationId) {
        return this.getAdapter(messageType).send(recipients, body, correlationId);
    };
    /**
     * @returns all the unread message of any conversation in which the recipient is involved
     */
    MessagingPlatformService.prototype.getAllUnreadMessages = function () {
        return from(this.activeProviderAdapters.values()).pipe(mergeMap(function (adapter) { return adapter.getAllUnreadMessages(); }), mergeAll(), toArray());
    };
    /**
     * @returns a count of all the unread messages from all the conversations
     */
    MessagingPlatformService.prototype.getAllUnreadMessagesCount = function () {
        return forkJoin(Array.from(this.activeProviderAdapters.values()).map(function (adapter) { return adapter.getAllUnreadMessagesCount(); })).pipe(mergeAll());
    };
    /**
     * @param messageId unique identifier to represent a message
     * @param messageType the messaging type to delete the conversation from
     * @returns deleted message with an empty body
     */
    MessagingPlatformService.prototype.deleteMessage = function (messageId, messageType) {
        return this.getAdapter(messageType).deleteMessage(messageId);
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     * @param messageType the messaging type to delete the conversation from
     */
    MessagingPlatformService.prototype.deleteConversation = function (conversationId, messageType) {
        return this.getAdapter(messageType).deleteConversation(conversationId);
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     * @param messageType the messaging type to delete the conversation from
     */
    MessagingPlatformService.prototype.createConversation = function (conversationId, messageType) {
        return this.getAdapter(messageType).createConversation(conversationId);
    };
    /**
     * @param messageId unique identifier to represent a message
     * @param conversationId unique identifier to represent a conversation
     * @param messageType the messaging type to mark the conversation as read from
     */
    MessagingPlatformService.prototype.markConversationAsRead = function (messageId, conversationId, messageType) {
        return this.getAdapter(messageType).markConversationAsRead(messageId, conversationId);
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     * @param folder represents a folder where the conversation will be allocated
     * @param messageType the messaging type to mark the conversation as read from
     */
    MessagingPlatformService.prototype.updateConversationFolder = function (conversationId, folder, messageType) {
        return this.getAdapter(messageType).updateConversationFolder(conversationId, folder);
    };
    /**
     *
     */
    MessagingPlatformService.prototype.markAllConversationsAsRead = function (conversations) {
        var _this = this;
        return from(conversations.values()).pipe(filter(function (conversation) { return conversation.metadata.unreadMessagesCount > 0; }), mergeMap(function (conversation) {
            return _this.markConversationAsRead(conversation.metadata.latestMessage.id, conversation.conversationId, conversation.type);
        }), toArray());
    };
    /**
     * @param subscriptionId unique identifier to represent a notification channel subscription
     * @param eventTypes an array of strings representing events for subscription
     * @param messagingResource a unique resource that can be observed for events
     * @param events$ an observable that can be connected to for relevant events that will be provided by the notification channel
     * @param messageType the messaging type used for the subscription
     * @returns created subscription
     */
    MessagingPlatformService.prototype.createSubscription = function (subscriptionId, eventTypes, messagingResource, messageType) {
        return this.getAdapter(messageType).createSubscription(subscriptionId, eventTypes, messagingResource);
    };
    /**
     * @param events$ an observable that can be connected to for relevant events that will be provided by the notification channel
     * @param messageType the messaging type used for the subscription
     * @returns mapped events
     */
    MessagingPlatformService.prototype.registerEventSource = function (events$, messageType) {
        return this.getAdapter(messageType).registerEventSource(events$);
    };
    /**
     * @param subscriptionId unique identifier to represent a notification channel subscription
     * @param messageType the messaging type used for the subscription
     */
    MessagingPlatformService.prototype.deleteSubscription = function (subscriptionId, messageType) {
        return this.getAdapter(messageType).deleteSubscription(subscriptionId);
    };
    /**
     * @param conversationId unique identifier to represent a group chat
     * @param members the list of members to be added to the group chat
     * @param messageType the messaging type used for the subscription
     */
    MessagingPlatformService.prototype.addMembers = function (conversationId, members, messageType) {
        return this.getAdapter(messageType).addMembers(conversationId, members);
    };
    /**
     * @param messageType the messaging type used for the subscription
     * @param metadata the event metadata
     */
    MessagingPlatformService.prototype.getSendMessageCompleteLogEvent = function (messageType, metadata) {
        return this.getAdapter(messageType).getSendMessageCompleteLogEvent(metadata);
    };
    MessagingPlatformService.prototype.getAdapter = function (messageType) {
        var activeProvider = this.activeProviderAdapters.get(messageType);
        if (!activeProvider) {
            throw new Error("Error in messaging platform service: No provider active for message type " + messageType);
        }
        return activeProvider;
    };
    return MessagingPlatformService;
}());

export { MessagingPlatformService };
